import { Icon } from "@iconify/react/dist/iconify.js";
import { useState, useEffect } from "react";
import { useTickets } from "../../contexts/TicketContext";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { fetchAllAccounts } from "../../api/ticketData";

import Overview from "../../components/tickets/Overview";
import TicketLogs from "../../components/tickets/TicketLogs";
import NewTicket from "../../components/tickets/NewTicket";

// A new component to prompt the user to connect to Zoho
const ZohoAuthPrompt = ({ authUrl }) => {
    // Function to handle manual connection
    const handleConnect = (e) => {
        e.preventDefault();
        
        if (authUrl) {
            // Save the current page's path to return to after the Zoho auth flow
            sessionStorage.setItem('zoho_auth_return_url', window.location.pathname);
            
            // Open in the same window to prevent popup blockers
            window.location.href = authUrl;
        }
    };

    return (
        <div className="flex flex-col items-center justify-center text-center p-8 m-8 bg-bgSecondary shadow-neo-card-inner rounded-md">
            <Icon icon="logos:zoho" className="text-6xl mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Connect to Zoho Desk</h2>
            <p className="text-gray-400 mb-4">To view and create support tickets, you need to authorize this application with your Zoho account.</p>
            <p className="text-yellow-400 text-sm mb-6">
                <Icon icon="material-symbols:info-outline" className="inline mr-1" />
                Your session with Zoho may have expired. This is normal and requires re-authentication periodically.
            </p>
            <button 
                onClick={handleConnect}
                className="bg-primary text-white font-bold py-3 px-6 rounded-lg hover:bg-primary-dark transition-colors"
            >
                Connect Now
            </button>
        </div>
    );
};

const TicketsPage = () => {
    const { isLoading, error, zohoAuth, loadTickets, adminAccountFilter, setAdminAccountFilter } = useTickets();
    const user = useAuthUser();
    const [activeTab, setActiveTab] = useState('overview');

    // Account filter state (only for helios admin)
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accountsError, setAccountsError] = useState(null);

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        tabs: false,
        content: false
    });

    // Check if user is helios admin
    const isHeliosAdmin = user?.role === 'helios_admin' || user?.roles?.includes('helios_admin');

    // Load accounts for filter dropdown (only for helios admin)
    const loadAccounts = async () => {
        if (!isHeliosAdmin) return;

        setAccountsLoading(true);
        setAccountsError(null);
        
        try {
            const response = await fetchAllAccounts();
            // Transform accounts to have consistent structure with accountId and accountName
            const formattedAccounts = response.map(account => ({
                accountId: account.accountId || account.id,
                accountName: account.accountName || account.name,
            }));
            const sortedAccounts = formattedAccounts.sort((a, b) => a.accountName.localeCompare(b.accountName));
            setAccounts(sortedAccounts);
        } catch (error) {
            console.error("Failed to load accounts:", error);
            setAccountsError("Failed to load clients.");
            // Fallback to empty array
            setAccounts([]);
        } finally {
            setAccountsLoading(false);
        }
    };

    // Load accounts when component mounts or user changes
    useEffect(() => {
        if (user && isHeliosAdmin) {
            loadAccounts();
        }
    }, [user, isHeliosAdmin]);

    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];

        // Stagger the animations with delays
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, tabs: true }));
        }, 300));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, content: true }));
        }, 500));

        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    // Listen for page reset events from navbar
    useEffect(() => {
        const handlePageReset = (event) => {
            // Check if this page needs to reset based on its current state
            // If we're not on the overview tab, reset to overview
            if (activeTab !== 'overview') {
                setActiveTab('overview');
            }
        };

        window.addEventListener('pageReset', handlePageReset);

        return () => {
            window.removeEventListener('pageReset', handlePageReset);
        };
    }, [activeTab]);

    // Trigger content animation when tab changes
    useEffect(() => {
        setAnimationStates(prev => ({ ...prev, content: false }));
        const timeout = setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, content: true }));
        }, 100);

        return () => clearTimeout(timeout);
    }, [activeTab]);

    const isGatewayError = error && error.includes("gateway") && error.includes("temporarily unavailable");

    const renderTabContent = () => {
        // Conditionally render based on the context's state
        if (isLoading) return <div className="text-center p-10">Loading tickets...</div>;
        if (zohoAuth.required) return <ZohoAuthPrompt authUrl={zohoAuth.url} />;
        if (error) {
            return (
                <div className="flex flex-col items-center justify-center text-center p-8 m-4 bg-bgSecondary shadow-neo-card-inner rounded-md max-w-lg mx-auto">
                    <Icon icon="mdi:connection" className="text-5xl text-amber-400 mb-3" />
                    <h3 className="text-lg font-semibold text-white mb-2">Tickets temporarily unavailable</h3>
                    <p className="text-gray-400 mb-4">{error}</p>
                    {isGatewayError && (
                        <p className="text-gray-500 text-sm mb-4">This is often temporary. Try again in a moment.</p>
                    )}
                    <button
                        type="button"
                        onClick={() => loadTickets()}
                        className="bg-primary text-white font-medium py-2 px-4 rounded-lg hover:opacity-90 transition-opacity"
                    >
                        Try again
                    </button>
                </div>
            );
        }

        switch (activeTab) {
            case 'overview': return <Overview />;
            case 'log': return <TicketLogs />;
            case 'new': return <NewTicket />;
            default: return <Overview />;
        }
    };

    return (
        <div className="responsive-page-layout scrollable">
            {/* Header */}
            <div className={`dashboard-header-responsive transition-all duration-700 ease-out ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                <div className="flex flex-col gap-2 items-start w-full">
                    <h1 className="header-textbox">
                        <Icon icon="f7:tickets" className="header-icon" />
                        <span className="text-font-primary">Tickets</span>
                    </h1>
                    <p className="text-font-secondary text-sm sm:text-base">
                        Manage support tickets and track issues
                    </p>
                </div>
                {/* Client Filter (Only visible to Helios Admin) */}
                {isHeliosAdmin && (
                    <div className={`flex-shrink-0 transition-all duration-500 ease-out ${
                        animationStates.header
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-4'
                    }`}>
                        <div className="flex flex-row gap-2 items-center w-fit">
                            <select
                                value={adminAccountFilter}
                                onChange={(e) => setAdminAccountFilter(e.target.value)}
                                className="p-2 dropdown-container"
                                disabled={accountsLoading}
                            >
                                <option value="all">{accountsLoading ? 'Loading Clients...' : 'All Clients'}</option>
                                {accounts.map(account => (
                                    <option key={account.accountId} value={account.accountId}>
                                        {account.accountName}
                                    </option>
                                ))}
                            </select>
                            
                            {accountsError && (
                                <span className="text-orange-500 text-sm">⚠️ {accountsError}</span>
                            )}
                            
                            {adminAccountFilter !== 'all' && (
                                <button
                                    onClick={() => setAdminAccountFilter('all')}
                                    className="flex flex-row items-center gap-1 p-2 bg-bgSecondary shadow-neo-card text-sm rounded-md hover:shadow-neo-card-inner transition-colors"
                                >
                                    <Icon icon="material-symbols:refresh" className="text-sm text-accent" />
                                    Reset Filter
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            

            {/* Content */}
            <div className={`w-full flex-1 flex flex-col gap-3 min-h-0 transition-all duration-500 ease-out ${
                animationStates.content
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                {/* Tab Navigation */}
                <div className={`w-full flex-shrink-0 transition-all duration-500 ease-out ${
                    animationStates.tabs
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                }`}>
                    <div className="tickets-tab-responsive">
                        <button
                            className={`tickets-tab-button ${activeTab === 'overview' ? 'tickets-tab-active' : 'tickets-tab-inactive'}`}
                            onClick={() => setActiveTab('overview')}
                        >
                            <Icon icon="ooui:text-summary-ltr" className={`responsive-icon-sm ${activeTab === 'overview' ? 'text-secondary' : 'text-font-secondary'}`} />
                            <span className="tickets-tab-text">Overview</span>
                        </button>
                        <button
                            className={`tickets-tab-button ${activeTab === 'log' ? 'tickets-tab-active' : 'tickets-tab-inactive'}`}
                            onClick={() => setActiveTab('log')}
                        >
                            <Icon icon="tabler:logs" className={`responsive-icon-sm ${activeTab === 'log' ? 'text-secondary' : 'text-font-secondary'}`} />
                            <span className="tickets-tab-text">Ticket Log</span>
                        </button>
                        <button
                            className={`tickets-tab-button ${activeTab === 'new' ? 'tickets-tab-active' : 'tickets-tab-inactive'}`}
                            onClick={() => setActiveTab('new')}
                        >
                            <Icon icon="material-symbols-light:new-label" className={`responsive-icon-sm ${activeTab === 'new' ? 'text-secondary' : 'text-font-secondary'}`} />
                            <span className="tickets-tab-text">New Ticket</span>
                        </button>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="flex flex-1 gap-2 h-fit">
                    {/* Main Content */}
                    <div className={`flex flex-col w-full transition-all duration-300 ease-out
                    `}>
                        {renderTabContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketsPage;